import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { getHistory } from '../store/building.action';
import { selectHistoryTimeline } from '../store/building.selector';
import { EcabEntry, HISTORY_TYPE, HistoryEntry, HistoryType } from '../model/history.model';
import { TimelineComponent } from '../timeline/timeline.component';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'sibat-history',
  template: `
    <div class="filter-bar">
      <span class="filter-chip" [class.active]="(activeFilter$ | async) === 'all'" (click)="setFilter('all')">{{
        'building.history.chips.all' | transloco
      }}</span>
      @for (filter of availableFilters$ | async; track filter) {
      <span class="filter-chip" [class.active]="(activeFilter$ | async) === filter.key" (click)="setFilter(filter.key)">{{
        filter.label
      }}</span>
      }
    </div>
    <sibat-timeline [timelineData]="filteredTimeline$ | async"></sibat-timeline>
  `,
  styleUrls: ['history.component.scss'],
  imports: [TimelineComponent, TranslocoPipe, AsyncPipe],
})
export class HistoryComponent {
  activeFilter$ = new BehaviorSubject<string>('all');

  historyRecord$ = (this.route.parent ?? this.route).params.pipe(
    tap(({ buildingId }) => this.store.dispatch(getHistory({ buildingId }))),
    mergeMap(() => this.store.select(selectHistoryTimeline))
  );

  availableFilters$ = combineLatest([this.historyRecord$, this.translocoService.langChanges$]).pipe(
    map(([timeline]) => {
      const allEntries: HistoryEntry[] = [];
      timeline.forEach(entries => allEntries.push(...entries));

      const filters: { key: string; label: string }[] = [];

      const t = (key: string) => this.translocoService.translate('building.history.' + key);

      const sibatTypes: { type: HistoryType; labelKey: string }[] = [
        { type: HISTORY_TYPE.typology, labelKey: 'chips.typology' },
        { type: HISTORY_TYPE.status, labelKey: 'chips.status' },
        { type: HISTORY_TYPE.itech, labelKey: 'chips.itech' },
      ];

      const hasControl = allEntries.some(
        e => e.type === HISTORY_TYPE.control || (e.type === HISTORY_TYPE.ecab && (e.data as EcabEntry).processingTheme === 'CONTROLE')
      );
      if (hasControl) {
        filters.push({ key: HISTORY_TYPE.control, label: t('chips.control') });
      }

      sibatTypes.forEach(({ type, labelKey }) => {
        if (allEntries.some(e => e.type === type)) {
          filters.push({ key: type, label: t(labelKey) });
        }
      });

      const ecabThemes = new Set<string>();
      allEntries
        .filter(e => e.type === HISTORY_TYPE.ecab)
        .forEach(e => {
          const theme = (e.data as EcabEntry).processingTheme;
          if (theme && theme !== 'CONTROLE') {
            ecabThemes.add(theme);
          }
        });
      ecabThemes.forEach(theme => {
        const translationKey = 'building.history.processingTheme.' + theme;
        const translated = this.translocoService.translate(translationKey);
        const label = translated !== translationKey ? translated : theme.replace(/_/g, ' ');
        filters.push({ key: theme, label });
      });

      return filters;
    })
  );

  filteredTimeline$ = combineLatest([this.historyRecord$, this.activeFilter$]).pipe(
    map(([timeline, filter]) => (filter === 'all' ? timeline : this.filterTimeline(timeline, filter)))
  );

  constructor(private store: Store, private route: ActivatedRoute, private translocoService: TranslocoService) {}

  setFilter(key: string): void {
    this.activeFilter$.next(key);
  }

  private filterTimeline(timeline: Map<string, HistoryEntry[]>, filter: string): Map<string, HistoryEntry[]> {
    const filtered = new Map<string, HistoryEntry[]>();
    timeline.forEach((entries, year) => {
      const matching = entries.filter(e => {
        if (filter === HISTORY_TYPE.control) {
          return e.type === HISTORY_TYPE.control || (e.type === HISTORY_TYPE.ecab && (e.data as EcabEntry).processingTheme === 'CONTROLE');
        }
        if (e.type === HISTORY_TYPE.ecab) {
          return (e.data as EcabEntry).processingTheme === filter;
        }
        return e.type === filter;
      });
      if (matching.length > 0) {
        filtered.set(year, matching);
      }
    });
    return filtered;
  }
}
